import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { X, Mail, ArrowRight, CheckCircle2, Sparkles, KeyRound, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
    onOpenRegulation?: () => void;
}

export const AuthModal = ({ isOpen, onClose, onOpenRegulation }: AuthModalProps) => {
    const [step, setStep] = useState<'email' | 'verify'>('email');
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resendTimer, setResendTimer] = useState(0);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setStep('email');
            setError(null);
            setEmail('');
            setOtp('');
            setLoading(false);
            setResendTimer(0);
        }
    }, [isOpen]);

    // Timer effect
    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(prev => prev - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendTimer]);

    // Handle ESC key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (isOpen && e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    const handleSendCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const referredBy = localStorage.getItem('referral_code');
            const authOptions = referredBy ? { data: { referred_by: referredBy } } : undefined;

            const { error } = await supabase.auth.signInWithOtp({
                email,
                options: authOptions
            });

            if (error) throw error;
            setStep('verify');
            setResendTimer(90);
        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Erro ao enviar código. Verifique o e-mail.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        if (resendTimer > 0) return;
        setLoading(true);
        setError(null);

        try {
            const referredBy = localStorage.getItem('referral_code');
            const authOptions = referredBy ? { data: { referred_by: referredBy } } : undefined;

            const { error } = await supabase.auth.signInWithOtp({
                email,
                options: authOptions
            });

            if (error) throw error;
            setResendTimer(90);
        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Erro ao reenviar código.');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const { error } = await supabase.auth.verifyOtp({
                email,
                token: otp,
                type: 'email',
            });

            if (error) throw error;
            // Success! The auth listener in App or elsewhere will handle the session
            // Clean up the referral code so it's not reused if user logs out & signs up again
            localStorage.removeItem('referral_code');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            onClose();
        } catch (err: any) {
            console.error(err);
            setError('Código inválido ou expirado. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-800 p-8 animate-in zoom-in-95 duration-200">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors z-10 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                    <X className="w-5 h-5" />
                </button>

                {step === 'email' ? (
                    <>
                        <div className="text-center mb-8">
                            <div className="flex items-center justify-center gap-3 mb-6">
                                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl text-blue-600 dark:text-blue-400">
                                    <Sparkles className="w-6 h-6" />
                                </div>
                                <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-400 dark:via-indigo-400 dark:to-purple-400">
                                    Radar da Economia
                                </span>
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                                Acesso Simplificado
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                Coloque seu e-mail e receba um código pra entrar.
                                <br />
                                Assim você libera todos os benefícios da plataforma!
                            </p>
                        </div>

                        <form onSubmit={handleSendCode} className="space-y-6">
                            <div className="space-y-4">
                                <Input
                                    label="Seu melhor e-mail"
                                    type="email"
                                    placeholder="exemplo@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoFocus
                                    icon={<Mail className="w-5 h-5" />}
                                    className="bg-gray-50 dark:bg-gray-800/50"
                                />

                                <Button
                                    type="submit"
                                    className="w-full h-12 text-base"
                                    isLoading={loading}
                                    loadingText="Enviando..."
                                >
                                    Receber Código <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </div>

                            {error && (
                                <div className="p-4 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-xl text-sm text-red-600 dark:text-red-400 text-center">
                                    {error}
                                </div>
                            )}

                            <p className="text-xs text-center text-gray-400 dark:text-gray-500">
                                Ao continuar, você concorda com nosso{' '}
                                <button
                                    type="button"
                                    onClick={onOpenRegulation}
                                    className="underline hover:text-blue-500 transition-colors"
                                >
                                    Regulamento
                                </button>.
                            </p>
                        </form>
                    </>
                ) : (
                    <>
                        <div className="text-center mb-8">
                            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4 text-green-600 dark:text-green-400">
                                <KeyRound className="w-6 h-6" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                Digite o Código
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                Enviamos um código de 6 dígitos para<br />
                                <strong className="text-gray-900 dark:text-white">{email}</strong>
                            </p>
                        </div>

                        <form onSubmit={handleVerifyCode} className="space-y-6">
                            <div className="space-y-4">
                                <Input
                                    label="Código de verificação"
                                    type="text"
                                    placeholder="000000"
                                    value={otp}
                                    onChange={(e) => {
                                        // Only allow numbers and limit to 6 chars
                                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                        setOtp(val);
                                    }}
                                    required
                                    autoFocus
                                    className="bg-gray-50 dark:bg-gray-800/50 text-center tracking-[0.5em] text-lg font-mono placeholder:tracking-normal"
                                    maxLength={6}
                                />

                                <Button
                                    type="submit"
                                    className="w-full h-12 text-base"
                                    isLoading={loading}
                                    loadingText="Verificando..."
                                >
                                    Confirmar Código <CheckCircle2 className="w-4 h-4 ml-2" />
                                </Button>

                                <div className="flex flex-col gap-3 mt-4">
                                    <button
                                        type="button"
                                        onClick={handleResendCode}
                                        disabled={resendTimer > 0 || loading}
                                        className="text-sm font-medium text-blue-600 hover:text-blue-500 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {resendTimer > 0
                                            ? `Reenviar código em ${resendTimer}s`
                                            : "Reenviar código"}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setStep('email')}
                                        className="flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-900 dark:hover:text-gray-300 transition-colors"
                                    >
                                        <ArrowLeft className="w-4 h-4" /> Corrigir e-mail
                                    </button>
                                </div>
                            </div>

                            {error && (
                                <div className="p-4 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-xl text-sm text-red-600 dark:text-red-400 text-center">
                                    {error}
                                </div>
                            )}
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};
